import React, { useContext, useEffect, useMemo, useState } from "react";
import UserContext from "../auth/UserContext";
import LocationDropdown from "../components/LocationDropdown";
import PodiumSnapshots from "./PodiumSnapshots";
import ValetActivityChartSelector from "./ValetActivityChartSelector";
import ValetActivityPieChartsSingle from "./ValetActivityPieChartsSingle";
import ValetActivityBarChartsSingle from "./ValetActivityBarChartsSingle";
import ValetActivityLineChartsSingle from "./ValetActivityLineChartsSingle";
import ValetActivityAreaChartsSingle from "./ValetActivityAreaChartsSingle";
import MultiLocationActivityChart from "./MultiLocationActivityChart";
import ValetApi from "../api/Api";
import { getDateInputValueInTimeZone } from "../utility/dateInput";
import "./Dashboard.css";
import ErrorBoundary from "../components/ErrorBoundary";

function parseIsoDate(isoDate) {
    return new Date(`${isoDate}T12:00:00`);
}

function toIsoDate(dateObj) {
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, "0");
    const day = String(dateObj.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
}

function shiftIsoDate(isoDate, deltaDays) {
    const dateObj = parseIsoDate(isoDate);
    dateObj.setDate(dateObj.getDate() + deltaDays);
    return toIsoDate(dateObj);
}

function getStartOfMonthIso(isoDate) {
    const dateObj = parseIsoDate(isoDate);
    dateObj.setDate(1);
    return toIsoDate(dateObj);
}

function getWeekdayLabel(isoDate) {
    try {
        return new Intl.DateTimeFormat("en-US", { weekday: "long" }).format(parseIsoDate(isoDate));
    } catch {
        return "Weekday";
    }
}

function buildDateList(startIso, endIso) {
    const start = parseIsoDate(startIso);
    const end = parseIsoDate(endIso);
    const normalizedStart = start <= end ? start : end;
    const normalizedEnd = start <= end ? end : start;
    const list = [];
    const cursor = new Date(normalizedStart);
    while (cursor <= normalizedEnd) {
        list.push(toIsoDate(cursor));
        cursor.setDate(cursor.getDate() + 1);
    }
    return list;
}

function buildDateFilter(preset, anchorDate, customStartDate, customEndDate) {
    const safeAnchor = anchorDate || getDateInputValueInTimeZone(new Date(), "UTC");

    if (preset === "past_week") {
        const startDate = shiftIsoDate(safeAnchor, -6);
        return {
            preset,
            startDate,
            endDate: safeAnchor,
            dates: buildDateList(startDate, safeAnchor),
            label: "Past Week",
        };
    }

    if (preset === "month_to_date") {
        const startDate = getStartOfMonthIso(safeAnchor);
        return {
            preset,
            startDate,
            endDate: safeAnchor,
            dates: buildDateList(startDate, safeAnchor),
            label: "Since Start Of Month",
        };
    }

    if (preset === "same_weekday_4") {
        const dates = [4, 3, 2, 1].map((weeksBack) => shiftIsoDate(safeAnchor, -(weeksBack * 7)));
        const weekday = getWeekdayLabel(safeAnchor);
        return {
            preset,
            startDate: dates[0],
            endDate: dates[dates.length - 1],
            dates,
            label: `Previous 4 ${weekday}s`,
        };
    }

    if (preset === "custom_range") {
        const startDate = customStartDate || safeAnchor;
        const endDate = customEndDate || safeAnchor;
        const dates = buildDateList(startDate, endDate);
        return {
            preset,
            startDate: dates[0],
            endDate: dates[dates.length - 1],
            dates,
            label: "Custom Date Range",
        };
    }

    return {
        preset: "today",
        startDate: safeAnchor,
        endDate: safeAnchor,
        dates: [safeAnchor],
        label: "Today",
    };
}

const Dashboard = () => {
    const { userDetail } = useContext(UserContext);
    const accessibleLocations = useMemo(() => userDetail?.accessibleLocations || [], [userDetail?.accessibleLocations]);
    const canViewMultiLocation = Number(userDetail?.roleId) >= 3 && accessibleLocations.length > 1;
    const locationOptions = useMemo(() => {
        return canViewMultiLocation ? [{ id: "all", name: "All Accessible Locations" }, ...accessibleLocations] : accessibleLocations;
    }, [canViewMultiLocation, accessibleLocations]);
    const userTimeZone = userDetail?.userTimeZone || Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
    const todayInUserTimeZone = getDateInputValueInTimeZone(new Date(), userTimeZone);
    const [selectedLocationId, setSelectedLocationId] = useState(accessibleLocations[0]?.id || "");
    const [locationPodiums, setLocationPodiums] = useState([]);
    const [podiumSnapshots, setPodiumSnapshots] = useState([]);
    const [locationRollups, setLocationRollups] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [selectedChart, setSelectedChart] = useState("bar");
    const [pendingPreset, setPendingPreset] = useState("today");
    const [pendingAnchorDate, setPendingAnchorDate] = useState(todayInUserTimeZone);
    const [pendingStartDate, setPendingStartDate] = useState(getStartOfMonthIso(todayInUserTimeZone));
    const [pendingEndDate, setPendingEndDate] = useState(todayInUserTimeZone);
    const [dateFilter, setDateFilter] = useState(() => buildDateFilter("today", todayInUserTimeZone, todayInUserTimeZone, todayInUserTimeZone));

    const pendingDateFilter = useMemo(() => {
        return buildDateFilter(pendingPreset, pendingAnchorDate, pendingStartDate, pendingEndDate);
    }, [pendingPreset, pendingAnchorDate, pendingStartDate, pendingEndDate]);

    const dateDirty = JSON.stringify(pendingDateFilter) !== JSON.stringify(dateFilter);
    const snapshotStartDate = dateFilter.startDate;
    const snapshotEndDate = dateFilter.endDate;
    const selectedDate = dateFilter.endDate;

    function formatDurationMinutes(value) {
        const numeric = Number(value);
        if (!Number.isFinite(numeric) || numeric < 0) return "--";
        return `${numeric.toFixed(1)} min`;
    }

    const applyDateFilter = () => {
        if (!dateDirty) return;
        setDateFilter(pendingDateFilter);
    };

    function renderRollupCards(rows) {
        return rows.map((row) => (
            <div
                key={row.rollupId || `${row.locationId}-${row.podiumId}`}
                className="dashboard-rollup-card">
                {(() => {
                    const capacity = row?.parkingCapacity == null ? null : Number(row.parkingCapacity);
                    const isCapacityValid = Number.isFinite(capacity) && capacity > 0;
                    const utilizationPct = isCapacityValid ? Math.min(999, Math.round((Number(row.activeOnDate || 0) / capacity) * 100)) : null;
                    return <div className="dashboard-rollup-row dashboard-rollup-row-capacity">{isCapacityValid ? `Lot Fullness: ${row.activeOnDate}/${capacity} (${utilizationPct}% full)` : "Lot Fullness: Capacity not configured"}</div>;
                })()}
                <div className="dashboard-rollup-card-title">
                    {row.locationName} - {row.podiumName}
                </div>
                <div className="dashboard-rollup-row dashboard-rollup-row-separator">Average Times:</div>
                <div className="dashboard-rollup-row">Check-In - Park: {formatDurationMinutes(row.avgCheckInToParkedMinutes)}</div>
                <div className="dashboard-rollup-row">Request - Retrieve: {formatDurationMinutes(row.avgRequestedToRetrievedMinutes)}</div>
                <div className="dashboard-rollup-row">Retrieve - Checkout: {formatDurationMinutes(row.avgRetrievedToCheckoutMinutes)}</div>
            </div>
        ));
    }

    function renderAtAGlanceStrip(rows) {
        if (!rows.length) return null;

        return (
            <div className="dashboard-at-a-glance-box">
                <div className="dashboard-rollup-title">At-A-Glance Status Snapshot ({dateFilter.label})</div>
                <div className="dashboard-at-a-glance-grid">
                    {rows.map((row) => (
                        <div
                            key={`glance-${row.rollupId || `${row.locationId}-${row.podiumId}`}`}
                            className="dashboard-at-a-glance-card">
                            <div className="dashboard-at-a-glance-card-title">{String(selectedLocationId) === "all" ? `${row.locationName} - ${row.podiumName}` : row.podiumName}</div>
                            <div className="dashboard-at-a-glance-status-grid">
                                <div className="dashboard-at-a-glance-status-pill">Checked In: {row.checkedInStatusCount}</div>
                                <div className="dashboard-at-a-glance-status-pill">Staged: {row.stagedStatusCount}</div>
                                <div className="dashboard-at-a-glance-status-pill">Parked: {row.parkedStatusCount}</div>
                                <div className="dashboard-at-a-glance-status-pill">Requested: {row.requestedStatusCount}</div>
                                <div className="dashboard-at-a-glance-status-pill">Retrieved: {row.retrievedStatusCount}</div>
                                <div className="dashboard-at-a-glance-status-pill">Checked Out: {row.checkedOutStatusCount}</div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    // Fetch podiums for selected location
    useEffect(() => {
        if (!selectedLocationId) return;
        if (String(selectedLocationId) === "all") {
            setLocationPodiums([]);
            setLoading(false);
            return;
        }
        setLoading(true);
        setError(null);
        setLocationPodiums([]);
        setPodiumSnapshots([]);
        setLocationRollups([]);
        ValetApi.getPodiumsByLocationId(selectedLocationId)
            .then((res) => {
                setLocationPodiums(res.data.podiums || []);
                setLoading(false);
            })
            .catch(() => {
                setLoading(false);
                setError("Failed to fetch podiums.");
            });
    }, [selectedLocationId]);

    // Fetch snapshots for all podiums at the location (single batched request)
    useEffect(() => {
        if (!selectedLocationId) return;
        setLoading(true);
        setPodiumSnapshots([]);
        setLocationRollups([]);
        setError(null);

        if (String(selectedLocationId) === "all") {
            Promise.all(
                accessibleLocations.map(async (loc) => {
                    const res = await ValetApi.getGarageSnapshotsByLocationId(loc.id, userTimeZone, null, snapshotStartDate, snapshotEndDate);
                    const snapshots = Array.isArray(res?.data?.snapshots) ? res.data.snapshots : [];
                    return { location: loc, snapshots };
                }),
            )
                .then((locationResults) => {
                    const flattened = [];
                    const rollups = [];

                    locationResults.forEach(({ location, snapshots }) => {
                        snapshots.forEach((row) => {
                            flattened.push({
                                podium: {
                                    id: `${location.id}-${row.podiumId}`,
                                    name: row.podiumName,
                                },
                                locationName: location.name,
                                snapshot: row,
                            });

                            const active = Number(row.activeOnDate || 0);
                            const completed = Number(row.checkedOutOnDate || 0);
                            rollups.push({
                                rollupId: `${location.id}-${row.podiumId}`,
                                locationId: location.id,
                                locationName: location.name,
                                podiumId: row.podiumId,
                                podiumName: row.podiumName,
                                parkingCapacity: row.parkingCapacity,
                                checkedOutOnDate: completed,
                                activeOnDate: active,
                                totalOnDate: active + completed,
                                checkedInStatusCount: Number(row.checkedInStatusCount || 0),
                                stagedStatusCount: Number(row.stagedStatusCount || 0),
                                parkedStatusCount: Number(row.parkedStatusCount || 0),
                                requestedStatusCount: Number(row.requestedStatusCount || 0),
                                retrievedStatusCount: Number(row.retrievedStatusCount || 0),
                                checkedOutStatusCount: Number(row.checkedOutStatusCount ?? completed),
                                avgCheckInToParkedMinutes: row.avgCheckInToParkedMinutes,
                                avgRequestedToRetrievedMinutes: row.avgRequestedToRetrievedMinutes,
                                avgRetrievedToCheckoutMinutes: row.avgRetrievedToCheckoutMinutes,
                            });
                        });
                    });

                    rollups.sort((a, b) => {
                        if (b.totalOnDate !== a.totalOnDate) return b.totalOnDate - a.totalOnDate;
                        if (a.locationName !== b.locationName) return a.locationName.localeCompare(b.locationName);
                        return String(a.podiumName || "").localeCompare(String(b.podiumName || ""));
                    });

                    setLocationRollups(rollups);
                    setPodiumSnapshots(flattened);
                    setLoading(false);
                })
                .catch(() => {
                    setLoading(false);
                    setError("Failed to fetch multi-location snapshots.");
                });

            return;
        }

        ValetApi.getGarageSnapshotsByLocationId(selectedLocationId, userTimeZone, null, snapshotStartDate, snapshotEndDate)
            .then((res) => {
                const snapshots = Array.isArray(res?.data?.snapshots) ? res.data.snapshots : [];
                const selectedLocation = accessibleLocations.find((loc) => String(loc.id) === String(selectedLocationId));
                const locationName = selectedLocation?.name || "Location";
                const results = snapshots.map((row) => ({
                    podium: {
                        id: row.podiumId,
                        name: row.podiumName,
                    },
                    snapshot: row,
                }));

                const rollups = snapshots
                    .map((row) => {
                        const active = Number(row.activeOnDate || 0);
                        const completed = Number(row.checkedOutOnDate || 0);
                        return {
                            rollupId: `${selectedLocationId}-${row.podiumId}`,
                            locationId: selectedLocationId,
                            locationName,
                            podiumId: row.podiumId,
                            podiumName: row.podiumName,
                            parkingCapacity: row.parkingCapacity,
                            checkedOutOnDate: completed,
                            activeOnDate: active,
                            totalOnDate: active + completed,
                            checkedInStatusCount: Number(row.checkedInStatusCount || 0),
                            stagedStatusCount: Number(row.stagedStatusCount || 0),
                            parkedStatusCount: Number(row.parkedStatusCount || 0),
                            requestedStatusCount: Number(row.requestedStatusCount || 0),
                            retrievedStatusCount: Number(row.retrievedStatusCount || 0),
                            checkedOutStatusCount: Number(row.checkedOutStatusCount ?? completed),
                            avgCheckInToParkedMinutes: row.avgCheckInToParkedMinutes,
                            avgRequestedToRetrievedMinutes: row.avgRequestedToRetrievedMinutes,
                            avgRetrievedToCheckoutMinutes: row.avgRetrievedToCheckoutMinutes,
                        };
                    })
                    .sort((a, b) => {
                        if (b.totalOnDate !== a.totalOnDate) return b.totalOnDate - a.totalOnDate;
                        return String(a.podiumName || "").localeCompare(String(b.podiumName || ""));
                    });

                setPodiumSnapshots(results);
                setLocationRollups(rollups);
                setLoading(false);
            })
            .catch(() => {
                setLoading(false);
                setError("Failed to fetch snapshots.");
            });
    }, [selectedLocationId, accessibleLocations, userTimeZone, snapshotStartDate, snapshotEndDate]);

    // Helper to render the correct chart type for a podium
    const renderChartForPodium = (podiumId) => {
        const chartKey = `${podiumId}-${dateFilter.preset}-${dateFilter.startDate}-${dateFilter.endDate}`;

        switch (selectedChart) {
            case "pie":
                return (
                    <ValetActivityPieChartsSingle
                        key={chartKey}
                        podiumId={podiumId}
                        selectedDate={selectedDate}
                        startDate={snapshotStartDate}
                        endDate={snapshotEndDate}
                        userTimeZone={userTimeZone}
                    />
                );
            case "bar":
                return (
                    <ValetActivityBarChartsSingle
                        key={chartKey}
                        podiumId={podiumId}
                        selectedDate={selectedDate}
                        startDate={snapshotStartDate}
                        endDate={snapshotEndDate}
                        userTimeZone={userTimeZone}
                    />
                );
            case "line":
                return (
                    <ValetActivityLineChartsSingle
                        key={chartKey}
                        podiumId={podiumId}
                        selectedDate={selectedDate}
                        startDate={snapshotStartDate}
                        endDate={snapshotEndDate}
                        userTimeZone={userTimeZone}
                    />
                );
            case "area":
                return (
                    <ValetActivityAreaChartsSingle
                        key={chartKey}
                        podiumId={podiumId}
                        selectedDate={selectedDate}
                        startDate={snapshotStartDate}
                        endDate={snapshotEndDate}
                        userTimeZone={userTimeZone}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <div className="Dashboard page-scroll">
            <div className="page-scroll-inner dashboard-page">
                <div className="dashboard-dropdown-bar">
                    <LocationDropdown
                        locations={locationOptions}
                        selectedLocationId={selectedLocationId}
                        onChange={setSelectedLocationId}
                    />
                    <div className="dashboard-location-form dashboard-date-controls">
                        <div
                            className="dashboard-preset-pills"
                            role="group"
                            aria-label="Date Presets">
                            {[
                                { value: "today", label: "Today" },
                                { value: "past_week", label: "Past Week" },
                                { value: "month_to_date", label: "Month To Date" },
                                { value: "same_weekday_4", label: "Prev 4 Same Weekday" },
                                { value: "custom_range", label: "Custom" },
                            ].map((preset) => (
                                <button
                                    key={preset.value}
                                    type="button"
                                    className={`dashboard-preset-pill${pendingPreset === preset.value ? " is-active" : ""}`}
                                    aria-pressed={pendingPreset === preset.value}
                                    onClick={() => setPendingPreset(preset.value)}>
                                    {preset.label}
                                </button>
                            ))}
                        </div>

                        <div className="dashboard-date-field dashboard-date-field-compact">
                            <label
                                htmlFor="dashboard-anchor-date"
                                className="dashboard-location-label">
                                As Of:
                            </label>
                            <input
                                id="dashboard-anchor-date"
                                className="dashboard-location-select"
                                type="date"
                                value={pendingAnchorDate}
                                onChange={(e) => setPendingAnchorDate(e.target.value)}
                            />
                        </div>

                        {pendingPreset === "custom_range" ? (
                            <>
                                <div className="dashboard-date-field dashboard-date-field-compact">
                                    <label
                                        htmlFor="dashboard-start-date"
                                        className="dashboard-location-label">
                                        Start:
                                    </label>
                                    <input
                                        id="dashboard-start-date"
                                        className="dashboard-location-select"
                                        type="date"
                                        value={pendingStartDate}
                                        onChange={(e) => setPendingStartDate(e.target.value)}
                                    />
                                </div>

                                <div className="dashboard-date-field dashboard-date-field-compact">
                                    <label
                                        htmlFor="dashboard-end-date"
                                        className="dashboard-location-label">
                                        End:
                                    </label>
                                    <input
                                        id="dashboard-end-date"
                                        className="dashboard-location-select"
                                        type="date"
                                        value={pendingEndDate}
                                        onChange={(e) => setPendingEndDate(e.target.value)}
                                    />
                                </div>
                            </>
                        ) : null}

                        <button
                            type="button"
                            className="dashboard-date-apply-btn dashboard-date-apply-btn-inline"
                            onClick={applyDateFilter}
                            disabled={!dateDirty}>
                            Apply Range
                        </button>
                    </div>
                </div>
                <div className="dashboard-date-range-label">Using: {dateFilter.label}</div>

                {locationRollups.length > 0 && renderAtAGlanceStrip(locationRollups)}

                <PodiumSnapshots
                    loading={loading}
                    error={error}
                    podiumSnapshots={podiumSnapshots}
                    accessibleLocations={accessibleLocations}
                    selectedLocationId={selectedLocationId}
                    selectedDate={selectedDate}
                    dateFilter={dateFilter}
                    userTimeZone={userTimeZone}
                />

                {String(selectedLocationId) === "all" && locationRollups.length > 0 && (
                    <>
                        <MultiLocationActivityChart
                            podiumSnapshots={podiumSnapshots}
                            selectedDate={dateFilter.label}
                        />
                        <div className="dashboard-rollup-box">
                            <div className="dashboard-rollup-title">All Locations Podium Rollup ({dateFilter.label})</div>
                            <div className="dashboard-rollup-grid">{renderRollupCards(locationRollups)}</div>
                        </div>
                    </>
                )}

                {String(selectedLocationId) !== "all" && (
                    <>
                        <div className="dashboard-performance-controls">
                            <div className="dashboard-rollup-title">Valet Performance Analysis</div>
                            <ValetActivityChartSelector
                                selectedChart={selectedChart}
                                onChange={setSelectedChart}
                            />
                        </div>

                        <div className="dashboard-charts-row">
                            {locationPodiums.map((podium) => (
                                <div
                                    key={podium.id}
                                    className="dashboard-chart-card">
                                    <h5>{podium.name}</h5>
                                    <ErrorBoundary>{renderChartForPodium(podium.id)}</ErrorBoundary>
                                </div>
                            ))}
                        </div>

                        {locationRollups.length > 0 && (
                            <div className="dashboard-rollup-box">
                                <div className="dashboard-rollup-title">
                                    {locationRollups[0].locationName} Podium Rollup ({dateFilter.label})
                                </div>
                                <div className="dashboard-rollup-grid">{renderRollupCards(locationRollups)}</div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default Dashboard;
